import { useState, useEffect, useRef } from "react";
import { Grid } from "@material-ui/core";
import axios from "axios";
import { useTranslation } from "react-i18next";
import httpClient from "../../lib/httpClient";
import { operationCancelled } from "../../lib/httpClient";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { filterModelToQueryValue, addExportButton } from "../util/tableUtils";
import { getValueToIconMapForHazard } from "../map/HazardSelectorHelper";
import i18n from "i18next";

import {
  mapHazardCodeToHazardProperty,
  riskLevelThresholdsAndLegends,
  getSimplifiedFromAnia,
  getSubintervals,
} from "../map/BivariateLogic";

import {
  capitalizeFirstLetterFormatter,
  fixedFormatter,
  oneDecimalFormatter,
  twoDecimalsFormatter,
  percent100Formatter,
  percent100twoDecimalsFormatter,
  fourDecimalsFormatter,
} from "../util/tableUtils";
import { hazardGaranzias } from "./BivariateLegend";
import { apiError } from "../../lib/apiError";

const aggregatedAdministrativeTableColumns = () => [
  // These columns are common for all aggregated tables
  {
    field: "accruedRisk",
    headerName: i18n.t("climateAnalytics.NUMERO_RISCHI_ANNO"),
    width: 60,
    ...oneDecimalFormatter,
  },
  {
    field: "postrenewalValueFinal",
    headerName: i18n.t("climateAnalytics.IMPORTO_SOMMA_VALORE_ASSICURATO_INDICIZZATO"),
    flex: 1,
    ...fixedFormatter,
  },
  {
    field: "premium",
    headerName: i18n.t("climateAnalytics.IMPORTO_PREMIO_COMPETENZA_PTF"),
    flex: 1,
    ...fixedFormatter,
  },
  {
    field: "claimCount",
    headerName: i18n.t("climateAnalytics.NUMERO_SINISTRI"),
    flex: 1,
    ...fixedFormatter,
  },
  {
    field: "claimLoadTotalAmount",
    headerName: i18n.t("climateAnalytics.IMPORTO_CARICO_TOTALE_SINISTRO"),
    flex: 1,
    ...fixedFormatter,
  },

  {
    field: "premio_compet_medio",
    headerName: i18n.t("climateAnalytics.PREMIO_MEDIO_COMPETENZA"),
    ...twoDecimalsFormatter,
  },
  {
    field: "costo_medio",
    headerName: i18n.t("climateAnalytics.COSTO_MEDIO"),
    ...fixedFormatter,
  },
  {
    field: "LR",
    headerName: i18n.t("climateAnalytics.LOSS_RATIO_RDNA"),
    ...percent100Formatter,
  },
  {
    field: "freqenza",
    headerName: i18n.t("climateAnalytics.FREQUENZA_OBS"),
    ...percent100twoDecimalsFormatter,
    hide: true, // This should be hidden if more than one garanzia is selected
  },
  {
    field: "grado_medio_danno",
    headerName: i18n.t("climateAnalytics.GRADO_MEDIO_DI_DANNO"),
    ...fourDecimalsFormatter,
  },
  {
    field: "tasso_obs",
    headerName: i18n.t("climateAnalytics.TASSO_OBS"),
    ...fourDecimalsFormatter,
  },
  {
    field: "tasso_pagato",
    headerName: i18n.t("climateAnalytics.TASSO_PAGATO"),
    ...fourDecimalsFormatter,
  },
  {
    field: "TIV_medio",
    headerName: i18n.t("climateAnalytics.IMPORTO_MEDIO_VALORE_ASSICURATO_INDICIZZATO"),
    ...fixedFormatter,
  },
  {
    field: "premio_puro_obs",
    headerName: i18n.t("climateAnalytics.PREMIO_PURO_OBS"),
    ...twoDecimalsFormatter,
  },

  {
    field: "policyNumber",
    headerName: i18n.t("climateAnalytics.policies"),
    flex: 1,
    ...fixedFormatter,
  },
];

const aggregatedCoverageTableColumns = () => [
  {
    field: "perilName",
    headerName: i18n.t("climateAnalytics.GARANZIA_RDNA"),
    width: 250,
    type: "str",
    ...capitalizeFirstLetterFormatter,
  },
  {
    field: "hazardlist",
    headerName: "hazardlist", // translator will be used here
    width: 100,
    type: "str",
  },
  ...aggregatedAdministrativeTableColumns(),
];

const aggregatedAgencyTableColumns = () => [
  {
    field: "agencyName",
    headerName: i18n.t("agency"),

    width: 200,
  },

  {
    field: "agencyCode",
    headerName: i18n.t("agencyCode"),
    width: 60,
  },
  ...aggregatedAdministrativeTableColumns(),
];
const aggregatedProvinceTableColumns = () => [
  {
    field: "addressProvinceName",
    headerName: i18n.t("state"),
    width: 20,
    flex: 1,
  },
  ...aggregatedAdministrativeTableColumns(),
];

const aggregatedCommuneTableColumns = () => [
  {
    field: "addressCity",
    headerName: i18n.t("city"),
    width: 20,
    flex: 1,
  },
  ...aggregatedAdministrativeTableColumns(),
];

const aggregatedBivariateTableColumnsFunc = (hazardSelect, KPISelect) => [
  {
    field: "commune",
    headerName: i18n.t("cityCode"),
    width: 150,
  },

  {
    field: "communeName",
    headerName: i18n.t("city"),
    width: 300,
  },

  {
    field: "hazardValue",
    headerName: headerForHazard(hazardSelect) + " value",
    width: 300,
  },
  {
    field: "hazardLevel",
    headerName: " level",
    width: 100,
  },
  {
    field: "kpiValue",
    headerName: headerForKPI(KPISelect),
    width: 300,

    ...fixedFormatter,
  },
  {
    field: "kpiLevel",
    headerName: headerForKPI(KPISelect) + " level",
    width: 200,
  },
];

const headerForHazard = (hazardSelect) => {
  if (hazardSelect === "anyhazard") {
    return i18n.t("climateAnalyticsSelects.anyHazard");
  } else {
    return getValueToIconMapForHazard(hazardSelect).text;
  }
};

const headerForKPI = (KPISelect) => {
  if (KPISelect === "none") {
    return i18n.t("climateAnalyticsSelects.numOfMunicipalities");
  } else {
    return i18n.t("climateAnalyticsSelects." + KPISelect);
  }
};

const getRiskLevelsTableColumns = (hazardSelect, KPISelect) => [
  {
    field: "level",
    headerName: headerForHazard(hazardSelect),
    width: 450,
  },
  {
    field: "kpiValue",
    headerName: headerForKPI(KPISelect),
    width: 400,
    ...fixedFormatter,
  },
  {
    field: "kpiValuePercents",
    headerName: "%", // name of the KPI will be here!
    width: 100,
    ...percent100twoDecimalsFormatter,
  },
];

const getAggregatedDataWrapper = async (aggregatedCancelTokenRef, getAggregatedData) => {
  // Cancel the previous request when the filterModel changes
  if (aggregatedCancelTokenRef.current) {
    aggregatedCancelTokenRef.current.cancel(operationCancelled);
  }

  // Create a new cancel token for the current request
  aggregatedCancelTokenRef.current = axios.CancelToken.source();
  getAggregatedData();
};

const getAggregatedData = async (
  setAggregatedTableState,
  setAggregatedTableData,
  aggrBy,

  filterModel,
  wantedReferenceDateProp,
  aggregatedCancelTokenRef,
) => {
  try {
    setAggregatedTableState((old) => ({ ...old, isLoading: true }));
    const load = {
      ...aggrBy,
      refDate: wantedReferenceDateProp,
      filters: filterModelToQueryValue(filterModel),
    };
    console.log("load", load);
    const { data: policiesResponse } = await httpClient().post(`analyticFilter/plainFilterPost`, load, {
      cancelToken: aggregatedCancelTokenRef.current.token,
    });

    if (policiesResponse?.error) {
      alert("You encountered an internal error, please try again later." + policiesResponse?.error);
      setAggregatedTableData([{ id: 0 }]);
    } else {
      setAggregatedTableData(policiesResponse?.plainResult);
    }

    setAggregatedTableState((old) => ({
      ...old,
      isLoading: false,
    }));
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      console.error("Error:", error);
      apiError(error);
      setAggregatedTableData([]);
      setAggregatedTableState((old) => ({ ...old, isLoading: false }));
    }
  }
};

const onFilterModelChange = (filterModel, getAggregatedDataWrapper) => {
  if (filterModel) {
    const isAnyIncompleteFilter = filterModel?.items?.some((filter) => !filter?.value);

    if (!isAnyIncompleteFilter) {
      getAggregatedDataWrapper();
    }
  } else {
    getAggregatedDataWrapper();
  }
};

const renderGrid = (
  aggregatedTableColumns,
  aggregatedTableData,
  aggregatedTableState,
  t,
  fileNamePrefix,
  props,
) => {
  return (
    <Grid item xs={12} sm={12}>
      <div style={{ width: "100%" }}>
        <DataGridCystellarTable
          density={"compact"}
          pageSizeOptions={[5, 25, 50, 100]}
          columns={aggregatedTableColumns}
          rows={aggregatedTableData}
          loading={aggregatedTableState.isLoading}
          customToolbarComp={true}
          customToolbarWidgets={addExportButton(fileNamePrefix)}
          {...props}
        />
      </div>
    </Grid>
  );
};

export const GridAggregatedByCoverage = ({ filterModel, wantedReferenceDateProp }) => {
  const { t } = useTranslation();
  const [aggregatedCoverageTableState, setAggregatedCoverageTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const aggregatedCoverageCancelTokenRef = useRef(null);

  const [aggregatedCoverageTableData, setAggregatedCoverageTableData] = useState([]);

  useEffect(() => {
    const getAggregatedCoverageData = async () => {
      return await getAggregatedData(
        setAggregatedCoverageTableState,
        setAggregatedCoverageTableData,
        { aggr: "Coverage" },
        filterModel,
        wantedReferenceDateProp,
        aggregatedCoverageCancelTokenRef,
      );
    };
    const getAggregatedCoverageDataWrapper = async () => {
      getAggregatedDataWrapper(aggregatedCoverageCancelTokenRef, getAggregatedCoverageData);
    };
    onFilterModelChange(filterModel, getAggregatedCoverageDataWrapper);
  }, [filterModel, wantedReferenceDateProp]);

  return renderGrid(
    aggregatedCoverageTableColumns(),
    aggregatedCoverageTableData,
    aggregatedCoverageTableState,
    t,
    "Garanzia",
  );
};

export const GridAggregatedByProvince = ({ filterModel, wantedReferenceDateProp }) => {
  const { t } = useTranslation();

  const [aggregatedProvinceTableState, setAggregatedProvinceTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const aggregatedProvinceCancelTokenRef = useRef(null);

  const [aggregatedProvinceTableData, setAggregatedProvinceTableData] = useState([]);

  useEffect(() => {
    const getAggregatedProvinceData = async () => {
      return await getAggregatedData(
        setAggregatedProvinceTableState,
        setAggregatedProvinceTableData,
        { aggr: "Province" },
        filterModel,
        wantedReferenceDateProp,
        aggregatedProvinceCancelTokenRef,
      );
    };
    const getAggregatedProvinceDataWrapper = async () => {
      getAggregatedDataWrapper(aggregatedProvinceCancelTokenRef, getAggregatedProvinceData);
    };

    onFilterModelChange(filterModel, getAggregatedProvinceDataWrapper);
  }, [filterModel, wantedReferenceDateProp]);

  return renderGrid(
    aggregatedProvinceTableColumns(),
    aggregatedProvinceTableData,
    aggregatedProvinceTableState,
    t,
    "Province",
  );
};

export const GridAggregatedByCommune = ({
  filterModel,
  wantedReferenceDateProp,
  setSelectedComuneIstatSetter,
}) => {
  const { t } = useTranslation();

  const [aggregatedCommuneTableState, setAggregatedCommuneTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const aggregatedCommuneCancelTokenRef = useRef(null);

  const [aggregatedCommuneTableData, setAggregatedCommuneTableData] = useState([]);

  const onRowClickCallback = (params) => {
    const row = params.row;
    setSelectedComuneIstatSetter(row.addressCityCode); // row.id would work same way. It is "999999" comune Istat code.
  };

  useEffect(() => {
    const getAggregatedCommuneData = async () => {
      return await getAggregatedData(
        setAggregatedCommuneTableState,
        setAggregatedCommuneTableData,
        { aggr: "Commune" },
        filterModel,
        wantedReferenceDateProp,
        aggregatedCommuneCancelTokenRef,
      );
    };
    const getAggregatedCommuneDataWrapper = async () => {
      getAggregatedDataWrapper(aggregatedCommuneCancelTokenRef, getAggregatedCommuneData);
    };

    onFilterModelChange(filterModel, getAggregatedCommuneDataWrapper);
  }, [filterModel, wantedReferenceDateProp]);

  return renderGrid(
    aggregatedCommuneTableColumns(),
    aggregatedCommuneTableData,
    aggregatedCommuneTableState,
    t,
    "Comuni",
    {
      onRowClick: onRowClickCallback,
    },
  );
};

export const GridAggregatedByAgency = ({ filterModel, wantedReferenceDateProp }) => {
  const { t } = useTranslation();

  const [aggregatedAgencyTableState, setAggregatedAgencyTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const aggregatedAgencyCancelTokenRef = useRef(null);

  const [aggregatedAgencyTableData, setAggregatedAgencyTableData] = useState([]);

  useEffect(() => {
    const getAggregatedAgencyData = async () => {
      return await getAggregatedData(
        setAggregatedAgencyTableState,
        setAggregatedAgencyTableData,
        { aggr: "Agency" },
        filterModel,
        wantedReferenceDateProp,
        aggregatedAgencyCancelTokenRef,
      );
    };
    const getAggregatedAgencyDataWrapper = async () => {
      getAggregatedDataWrapper(aggregatedAgencyCancelTokenRef, getAggregatedAgencyData);
    };

    onFilterModelChange(filterModel, getAggregatedAgencyDataWrapper);
  }, [filterModel, wantedReferenceDateProp]);

  return renderGrid(
    aggregatedAgencyTableColumns(),
    aggregatedAgencyTableData,
    aggregatedAgencyTableState,
    t,
    "Agenzie",
  );
};

export const GridBivariate = ({
  filterModel,
  hazardSelect,
  KPISelect,
  wantedReferenceDateProp,
  setSelectedComuneIstatSetter,
}) => {
  const { t } = useTranslation();

  const [aggregatedBivariateTableState, setAggregatedBivariateTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const aggregatedBivariateCancelTokenRef = useRef(null);

  const [aggregatedBivariateTableData, setAggregatedBivariateTableData] = useState([]);

  const onRowClickCallback = (params) => {
    const row = params.row;
    setSelectedComuneIstatSetter(row.commune); // row.id would work same way. It is "999999" comune Istat code.
  };

  useEffect(() => {
    const getAggregatedBivariateData = async () => {
      const cysHazardSelect = mapHazardCodeToHazardProperty[hazardSelect];

      const subIntervals = getSubintervals(KPISelect, hazardSelect);
      return await getAggregatedData(
        setAggregatedBivariateTableState,
        setAggregatedBivariateTableData,
        // `aggrByMap~${hazardSelect}~${cysHazardSelect}~${KPISelect}~${lowValue}~${highValue}~${subIntervals}`,
        {
          aggr: "Map",
          hazardSelect: getSimplifiedFromAnia(hazardSelect),
          cysHazardName: cysHazardSelect,
          KPISelect,
          subIntervals,
        },
        filterModel,
        wantedReferenceDateProp,
        aggregatedBivariateCancelTokenRef,
      );
    };

    const getAggregatedBivariateDataWrapper = async () => {
      getAggregatedDataWrapper(aggregatedBivariateCancelTokenRef, getAggregatedBivariateData);
    };

    onFilterModelChange(filterModel, getAggregatedBivariateDataWrapper);
  }, [filterModel, hazardSelect, KPISelect, wantedReferenceDateProp]);

  const aggregatedBivariateTableColumns = aggregatedBivariateTableColumnsFunc(hazardSelect, KPISelect);
  return renderGrid(
    aggregatedBivariateTableColumns,
    aggregatedBivariateTableData,
    aggregatedBivariateTableState,
    t,
    "Bivariata",
    {
      onRowClick: onRowClickCallback,
    },
  );
};

export const GridRiskLevels = ({ filterModel, hazardSelect, KPISelect, wantedReferenceDateProp }) => {
  const { t } = useTranslation();
  const subIntervalsJson = riskLevelThresholdsAndLegends(t)[hazardSelect].riskLevelValues;
  const [riskLevelsTableState, setRiskLevelsTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const riskLevelsCancelTokenRef = useRef(null);
  const [riskLevelsTableData, setRiskLevelsTableData] = useState([]);
  const [isComputing, setIsComputing] = useState(false);

  useEffect(() => {
    const subIntervalsJsonInternal = riskLevelThresholdsAndLegends(t)[hazardSelect].riskLevelValues;
    // This must be called inside useEffect to avoid infinite loop. It is because it returns an array.

    const getRiskLevelsData = async () => {
      const cysHazardSelect = mapHazardCodeToHazardProperty[hazardSelect];
      setIsComputing(true);
      const riskLevelsData = await getAggregatedData(
        setRiskLevelsTableState,
        setRiskLevelsTableData,
        // `aggrByRisk~${hazardSelect}~${cysHazardSelect}~${KPISelect}~${lowValue}~${highValue}~${subIntervalsAsString}`,
        {
          aggr: "Risk",
          hazardSelect: getSimplifiedFromAnia(hazardSelect),
          cysHazardName: cysHazardSelect,
          KPISelect,
          subIntervals: subIntervalsJsonInternal,
        },
        filterModel,
        wantedReferenceDateProp,
        riskLevelsCancelTokenRef,
      );
      setIsComputing(false);
      return riskLevelsData;
    };
    const getRiskLevelsDataWrapper = async () => {
      getAggregatedDataWrapper(riskLevelsCancelTokenRef, getRiskLevelsData);
    };

    onFilterModelChange(filterModel, getRiskLevelsDataWrapper);
  }, [filterModel, hazardSelect, KPISelect, wantedReferenceDateProp, t]);

  const addTitlesForRows = (riskLevelsTableData) => {
    let riskLevelsTableDataTranslated = riskLevelsTableData;
    if (!isComputing && riskLevelsTableDataTranslated.length > 0) {
      const nRows = riskLevelsTableData.length;

      // Keep this sync with back-end code in  createKPITable.
      if (subIntervalsJson.strictStepValues) {
        // if  subIntervalsJson are "strictStepValues":[5,7] then two sums are for 5,7, we use 2+4 bins.

        // nRows == subIntervalsJson.strictStepValues.length + 4

        for (let row = 0; row < nRows - 4; row++) {
          riskLevelsTableDataTranslated[row].level =
            riskLevelThresholdsAndLegends(t)[hazardSelect].riskNames[row];
        }
      } else if (subIntervalsJson.intervalBoundaries) {
        // if  subIntervalsJson are "intervalBoundaries":[5,7] then three sums are for -Inf-5, 5-7, 7-Inf, we use 2+1+4 bins.

        // nRows !== subIntervalsJson.intervalBoundaries.length + 5) {

        for (let row = 0; row < nRows - 5; row++) {
          riskLevelsTableDataTranslated[row].level =
            riskLevelThresholdsAndLegends(t)[hazardSelect].riskNames[row];
        }
        riskLevelsTableDataTranslated[nRows - 5].level = t(
          "riskLevelAnalyticsTableEntries.higherThanUpperBound",
        );
      } else {
        console.error("Error in riskLevelsTableDataTranslated", subIntervalsJson);
      }
      // Then 4 extra entries were added for corner cases and the sum.
      riskLevelsTableDataTranslated[nRows - 4].level = t("riskLevelAnalyticsTableEntries.noMeasurements");
      riskLevelsTableDataTranslated[nRows - 3].level = t("riskLevelAnalyticsTableEntries.obsolete");
      riskLevelsTableDataTranslated[nRows - 2].level = t("riskLevelAnalyticsTableEntries.noAddress");
      riskLevelsTableDataTranslated[nRows - 1].level = t("riskLevelAnalyticsTableEntries.total");
    }
    return riskLevelsTableDataTranslated;
  };

  let riskLevelsTableDataTranslated = addTitlesForRows(riskLevelsTableData);
  const explainRiskLevels = (KPISelect, hazardSelect) => {
    return (
      <Grid item xs={12} sm={12}>
        <ul>
          <li>
            {i18n.t("analyticsLevels.thisKPIIs")} <b>{headerForKPI(KPISelect)}</b>{" "}
            {i18n.t("analyticsLevels.inThe")} {i18n.t("climateAnalytics.referenceDate")}{" "}
            <b>{wantedReferenceDateProp}</b>.
          </li>
          {KPISelect === "none" ? (
            <li>{i18n.t("analyticsLevels.value1")}</li>
          ) : (
            <li>{i18n.t("analyticsLevels.matchOnly")} </li>
          )}
          <li>
            {i18n.t("analyticsLevels.hazardConsidered")} <b>{headerForHazard(hazardSelect)}</b>
            {i18n.t("analyticsLevels.hazardConsidered2")}{" "}
            <a href="/#/riskassessment" target="other">
              (Link)
            </a>
            . {i18n.t("analyticsLevels.centre")}
          </li>
          <li>
            {i18n.t("analyticsLevels.coveragesForHazard")} <b>{headerForHazard(hazardSelect)}</b> :{" "}
            {hazardGaranzias(hazardSelect, KPISelect)}
          </li>
        </ul>
      </Grid>
    );
  };

  const riskLevelsTableColumns = getRiskLevelsTableColumns(hazardSelect, KPISelect);
  return (
    <Grid container>
      {explainRiskLevels(KPISelect, hazardSelect)}
      {/* The table has small number of rows, therefore pagination is turned off */}
      {renderGrid(
        riskLevelsTableColumns,
        riskLevelsTableDataTranslated,
        riskLevelsTableState,
        t,
        "Livelli_Rischio_" + KPISelect + "_" + hazardSelect,
        {
          pagination: false,
        },
      )}
    </Grid>
  );
};
